import React from 'react';
import { Link, withRouter } from 'react-router-dom';
import {
  AppBar,
  Button,
  Toolbar,
  IconButton,
  Hidden,
  Drawer,
  Typography
} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import RefreshIcon from '@material-ui/icons/Refresh';
import PropTypes from 'prop-types';

// Redux
import { connect } from 'react-redux';
import { setSchoolStatus, clearSchoolStatus } from '../redux/actions/statusActions';
import { logoutUser, checkAuthentication } from '../redux/actions/authActions';
import { checkSetupMode } from '../redux/actions/setupModeActions';
import { setSuccess, setAlert, clearErrors } from '../redux/actions/alertActions';

// Assets
import Logo from '../images/LogoWhiteBackground.png';

// Components
import SideMenu from './SideMenu';
import ConfirmStatusModal from '../components/ConfirmStatusModal';
import DashboardAppBar from './DashboardAppBar';

// Constants
import { AlertTypes, EMERGENCY_CALL_STATUS, EMERGENCY_TEST_CALL_STATUS, RED } from '../constants';
import { makeSchoolEmergencyCall , makeSchoolTestEmergencyCall, getSchoolAlertUpdatesCsvExport } from '../api';
import { DateTime } from 'luxon';

class AppToolBar extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      isSidePanelOpen: false,
      showConfirmationModal: false,
      desiredStatus: null,
      isCsvDownloading: false
    };
  }

  componentDidMount() {
    this.props.checkAuthentication(this.props.userID, this.props.isAuthenticated);
    if (this.props.isAuthenticated) this.props.checkSetupMode();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.userID !== this.props.userID || prevProps.isAuthenticated !== this.props.isAuthenticated) {
      this.props.checkAuthentication(this.props.userID, this.props.isAuthenticated);
      if (this.props.isAuthenticated) this.props.checkSetupMode();
    }
  }

  toggleSideMenu = () => {
    this.setState({ isSidePanelOpen: !this.state.isSidePanelOpen });
  }

  handleEmergencyCall = async()=> {
    try {
      const response = await makeSchoolEmergencyCall();
      console.log('EMER success', response, EMERGENCY_CALL_STATUS);
      this.props.setSuccess('Emergency Call Initiated Successfully');
    }
    catch (error) {
      console.log('EMER error', error);
      this.props.setError(error? error: 'Emergency Call Failed');
    }
    finally {
      this.props.clearErrors();
    }
  }

  handleAlertUpdateCsvExport = async(type)=> {
    try {

      this.setState({
        isCsvDownloading: type || true
      });

      const response = await getSchoolAlertUpdatesCsvExport({
        timezone: DateTime.local().zoneName,
        type: type
      });
      
      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;

      if (type === 'Emergency') {
        a.download = 'Code-Red-Logs.csv';
      }
      else if (type === 'Admin') {
        a.download = 'Code-Yellow-Logs.csv';
      }
      else if (type === 'Medical') {
        a.download = 'Code-Blue-Logs.csv';
      }
      else {
        a.download = 'alerts-update.csv';
      }

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      console.log('CSV export success', response);
      this.props.setSuccess('CSV Exported Successfully');


    }
    catch (error) {
      console.log('CSV export error', error);
      this.props.setError(error? error: 'CSV Export Failed');
    }
    finally {
      this.setState({
        isCsvDownloading: false
      });
      this.props.clearErrors();
    }
  }

  
  handleTestEmergencyCall = async () => {
    try {
      const response = await makeSchoolTestEmergencyCall();
      console.log('EMER Test success', response, EMERGENCY_TEST_CALL_STATUS);
      this.props.setSuccess('Emergency Test Call Initiated Successfully');
    }
    catch (error) {
      console.log('EMER success', error);
      this.props.setError(error? error: 'Emergency Test Call Failed');
    }
    finally {
      this.props.clearErrors();
    }
  }

  handleOpenConfirmationModal = (desiredStatus) => {
    this.setState({ showConfirmationModal: true, desiredStatus: desiredStatus });
  };

  handleCloseConfirmationModal = () => {
    this.setState({ showConfirmationModal: false });
  };

  confirmStatus = async (status) => {
    if (status.dashboardStatus === EMERGENCY_CALL_STATUS) {
      console.log('EMER Initiated');
      await this.handleEmergencyCall();
    }
    else if (status.dashboardStatus === EMERGENCY_TEST_CALL_STATUS) {
      console.log('EMER Test Initiated');
      await this.handleTestEmergencyCall();
    }
    else {
      this.props.setSchoolStatus(status);
    }
    this.handleCloseConfirmationModal();
  }

  clearStatus = () => {
    this.props.clearSchoolStatus();
    this.handleCloseConfirmationModal();
  }

  refreshSchoolStatus() {
    window.location.reload(false);
  }

  render() {
    return (
      <>
        <ConfirmStatusModal
          closeModal={this.handleCloseConfirmationModal}
          showModal={this.state.showConfirmationModal}
          setStatus={this.confirmStatus}
          clearStatus={this.clearStatus}
          desiredStatus={this.state.desiredStatus}
        />

        <AppBar style={{ backgroundColor: this.props.schoolStatus.getColor().main }} position='static'>
          <Toolbar style={{ display: 'flex', justifyContent: 'space-between' }}>

            {/* --- LOGO --- */}
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <Link to='/'>
                <img
                  height={'40px'}
                  mode={'fill'}
                  src={Logo}
                  alt={'Logo'}
                  style={{ marginTop: '5px', marginBottom: '5px', borderRadius: '5px' }}
                />
              </Link>

              {/* --- STATUS --- */}
              <div style={{ marginLeft: '15px' }}>
                <Typography style={{ color: this.props.schoolStatus.getColor().dark, fontSize: '20pt', fontWeight: 'bold' }}>
                  {this.props.schoolStatus && this.props.schoolStatus !== AlertTypes.NORMAL &&
                    this.props.schoolStatus.getDashboardStatus().toUpperCase()
                  }
                </Typography>
              </div>
            </div>

            {/* --- BUTTONS --- */}
            <div>
              {this.props.location.pathname !== '/' && this.props.isAuthenticated && !this.props.isMobile &&
                <Button href={'/'} style={{ color: this.props.schoolStatus.getColor().text, textTransform: 'none', marginRight: '10px' }}>Dashboard</Button>}

              {this.props.isAuthenticated &&
              <Hidden xsDown>
                <Button
                  onClick={() => this.handleOpenConfirmationModal(AlertTypes.EMERGENCY)}
                  style={{
                    backgroundColor: this.props.schoolStatus.getColor() === RED ? RED.dark : RED.main, color: RED.text,
                    textTransform: 'none',
                    fontSize: '15px',
                    fontWeight: 'BOLD',
                  }}>
                  Set Emergency Status
                </Button>
              </Hidden>}

              <IconButton
                aria-haspopup='true'
                onClick={this.refreshSchoolStatus.bind(this)}
                disabled={this.props.isLoadingSchoolStatus}>
                <RefreshIcon style={{ color: this.props.schoolStatus.getColor().text }} />
              </IconButton>

              {this.props.isAuthenticated &&
              <IconButton
                aria-controls='nav-menu'
                aria-haspopup='true'
                onClick={this.toggleSideMenu}>
                <MenuIcon style={{ color: this.props.schoolStatus.getColor().text }}/>
              </IconButton>}

              {this.props.isAuthenticated &&
              <Drawer anchor={'right'} open={this.state.isSidePanelOpen} onClose={this.toggleSideMenu}>
                <SideMenu
                  exportAlertUpdateCsv={this.handleAlertUpdateCsvExport}
                  toggle={this.toggleSideMenu}
                  openConfirmationModal={this.handleOpenConfirmationModal}
                  logout={this.props.logout}
                  userType={this.props.userType}
                  isMobile={this.props.isMobile}
                  isCsvDownloading={this.state.isCsvDownloading}
                />
              </Drawer>}
            </div>
          </Toolbar>
        </AppBar>

        {this.props.location?.pathname?.includes('/dashboard') &&
          <DashboardAppBar />
        }
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  schoolStatus: state.status.schoolStatus || AlertTypes.UNKNOWN,
  isLoadingSchoolStatus: state.status.isLoading,
  isAuthenticated: state.auth.isAuthenticated,
  userType: state.auth.userType,
  isMobile: state.ui.isMobile,
});

const mapDispatchToProps = dispatch => ({
  setSchoolStatus: (alertStatus) => setSchoolStatus(dispatch, alertStatus),
  clearSchoolStatus: () => clearSchoolStatus(dispatch),
  logout: () => {
    logoutUser(dispatch);

  },
  checkAuthentication: (userID, isAuthenticated) => checkAuthentication(dispatch, userID, isAuthenticated),
  checkSetupMode: () => checkSetupMode(dispatch),
  setError: (msg) => setAlert(dispatch, msg),
  setSuccess: (msg) => setSuccess(dispatch, msg),
  clearErrors: () => clearErrors()
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(AppToolBar));


AppToolBar.propTypes = {
  checkAuthentication: PropTypes.func.isRequired,
  checkSetupMode: PropTypes.func.isRequired,
  userID: PropTypes.number,
  isAuthenticated: PropTypes.bool,
  setSchoolStatus: PropTypes.func,
  isLoadingSchoolStatus: PropTypes.bool,
  clearSchoolStatus: PropTypes.func,
  schoolStatus: PropTypes.object,
  location: PropTypes.object,
  isMobile: PropTypes.bool,
  logout: PropTypes.func,
  userType: PropTypes.any,
  setSuccess: PropTypes.func,
  setError: PropTypes.func,
  clearErrors: PropTypes.func,
};
